// AreaCapture — the staging buffer for a single area within a report,
// before AI processing turns it into Findings + Evidence.
//
// One row per (report, areaName). photo URLs accumulate as the inspector
// captures, then ai-svc consumes the buffer and emits findings. The unique
// constraint (reportId, areaName) is intentional: capturing the same area
// twice should append, not duplicate.
import { isoDateTimeSchema } from "@bitacorapro/shared";
import { z } from "zod";
import { areaCaptureStatusSchema } from "../enums.js";
import { areaCaptureIdSchema, reportIdSchema } from "../ids.js";
// AudioNote — one inspector note, voice-transcribed or typed. Multiple
// notes per area are preserved as separate entries (#0077) so the UI can
// render them as distinct cards across refreshes. The AI consumer reads
// their shared `text` field when building the analysis prompt; per-note
// identity is strictly for FE affordance and editability.
//
// ADR-0096 — nota escrita. `kind` discriminates a voice-transcribed note
// from a typed one. Both arms share ONE content field, `text` — never a
// second field like `body` or `transcript` — because every downstream
// consumer (ai-svc's two handlers, the Revisión B projection, the acta)
// already reads `.text`, and a second content field would force
// `note.text ?? note.body` at every one of them while admitting the
// illegal state where both are set. `source` was considered and
// rejected: it would be 1:1 redundant with `kind`.
//
// `checklistItemId` MUST be on both arms. It is not cosmetic: the
// ADR-0088 dual-projection write (reports.ts's createAreaCapture) keys
// strictly on `note.checklistItemId` when appending to
// report_checklist_results.captured_audio_note_ids — that append is the
// entire mechanism by which a typed note counts toward `noteCount` with
// zero change to blocks.ts's itemEvidenceSatisfied. Drop it from the
// typed arm and every typed note silently stops counting as evidence.
const noteSharedFields = {
    // Whatever the client supplied (typically the recorder's or the
    // writer's internal id at capture time). Not constrained to a uuid —
    // hydrated notes can have synthetic ids and we don't want to reject
    // them on read.
    id: z.string().min(1).max(64),
    // When the user *finished* producing the note client-side (recording
    // stop, or "Guardar nota" tap), not a server timestamp. This is the
    // ordering key for the append-ordered notes list — kept on both arms
    // rather than renamed to `createdAt` on the typed arm so no sort site
    // needs to branch on `kind`.
    recordedAt: isoDateTimeSchema,
    /** ADR-0088 — which checklist item this note is evidence for. Nullable/
     *  optional: a section-level note (today's behavior, and the only shape
     *  residential_rental / broker_postventa ever produce) omits it. Must
     *  ride in the same request that commits the note — see the identical
     *  rule on reports.ts's uploadPhotoBody.checklistItemId. */
    checklistItemId: z.string().uuid().nullable().optional(),
};
export const voiceNoteSchema = z.object({
    ...noteSharedFields,
    kind: z.literal("voice"),
    text: z.string().max(20_000),
    durationSeconds: z
        .number()
        .nonnegative()
        .max(60 * 60),
});
export const typedNoteSchema = z.object({
    ...noteSharedFields,
    kind: z.literal("typed"),
    // Server ceiling deliberately tighter than the voice arm's 20_000 —
    // the UI caps the writer textarea at 1200 chars (maxlength, hard
    // limit); this is that cap's enforcement point, with headroom for
    // client/server drift, not the 20k ceiling that's legitimate for a
    // long transcript.
    text: z.string().trim().min(1).max(2_000),
    // Set only on an edit; a freshly-created typed note omits it rather
    // than inventing a value equal to `recordedAt`.
    updatedAt: isoDateTimeSchema.optional(),
});
export const audioNoteSchema = z.discriminatedUnion("kind", [voiceNoteSchema, typedNoteSchema]);
/** Rows persisted before the `kind` discriminant landed carry no `kind`
 *  key at all. `z.discriminatedUnion` dispatches on the RAW input value
 *  before any `.default()` would apply, so `kind: z.literal("voice")
 *  .default("voice")` does NOT tolerate a missing key — it still throws
 *  `invalid_union_discriminator`. Wrapping the union in `z.preprocess`
 *  doesn't work either: it returns a `ZodEffects`, which has no
 *  `.omit()`/`.extend()` (breaking every packages/api-types wire
 *  derivation) and which Fastify's Zod→JSON-Schema conversion can't
 *  render. So legacy tolerance is this named, deletable, call-it-
 *  yourself function instead of implicit schema behavior — called at
 *  every raw-JSONB read site before parsing. Every writer emits `kind`
 *  explicitly; only reads need to tolerate its absence. Delete once (if
 *  ever) a backfill proves audio_notes is kind-complete — JSONB
 *  tolerates the mixed shape indefinitely, so there's no forced
 *  deadline for that backfill to exist. */
export function normalizeNoteKind(raw) {
    if (raw !== null && typeof raw === "object" && !("kind" in raw)) {
        return { ...raw, kind: "voice" };
    }
    return raw;
}
/** Array-level counterpart of normalizeNoteKind, for the common case of
 *  normalizing a whole `audioNotes` JSONB array before parsing. Not a
 *  schema — a plain array map, so callers stay free to `.filter()` out
 *  entries that still fail `audioNoteSchema.safeParse` after normalizing
 *  (malformed rows), rather than one bad entry failing the whole array. */
export function normalizeNoteKinds(raw) {
    return Array.isArray(raw) ? raw.map(normalizeNoteKind) : [];
}
// Who set an area's condition (ADR-0138): the AI's proposal or the
// inspector's confirmation. An inspector value is never overwritten by AI.
export const areaConditionSourceSchema = z.enum(["ai", "inspector"]);
export const areaCaptureSchema = z.object({
    id: areaCaptureIdSchema,
    reportId: reportIdSchema,
    areaName: z.string().trim().min(1).max(100),
    // ADR-0068 §Security/compliance preconditions, item 3. Server-resolved
    // link into inspection_areas for catalog-driven captures (today:
    // facility's A→B flow) — never client-supplied, so it's absent from
    // areaCaptureUpsertSchema below. Null for verticals with no
    // inspection_areas catalog (residential_rental, broker_postventa),
    // where areaName alone remains the identity.
    inspectionAreaId: z.string().uuid().nullable(),
    areaOrder: z.number().int().nonnegative(),
    audioNotes: z.array(audioNoteSchema).default([]),
    photoUrls: z.array(z.string().url()),
    status: areaCaptureStatusSchema,
    errorMessage: z.string().max(2_000).optional(),
    // Condición del recinto (ADR-0138) — only for report types whose recipe
    // declares stateSource "area_condition". A recipe severity key, written
    // by ai-svc as a proposal (source "ai") and confirmed or changed by the
    // inspector (source "inspector", PATCH .../condition). Optional +
    // nullable: every other report type, and every capture written before
    // the column existed, simply has none. Server-written only — absent
    // from areaCaptureUpsertSchema below.
    conditionKey: z.string().min(1).max(64).nullable().optional(),
    conditionSource: areaConditionSourceSchema.nullable().optional(),
    conditionSummary: z.string().max(2_000).nullable().optional(),
    conditionConfirmedAt: isoDateTimeSchema.nullable().optional(),
    createdAt: isoDateTimeSchema,
});
export const areaCaptureUpsertSchema = areaCaptureSchema
    .omit({
    id: true,
    status: true,
    errorMessage: true,
    createdAt: true,
    // Server-resolved (see areaCaptureSchema above) — never client-supplied.
    inspectionAreaId: true,
    // Server-written (ADR-0138): the AI proposal or the inspector's PATCH.
    conditionKey: true,
    conditionSource: true,
    conditionSummary: true,
    conditionConfirmedAt: true,
})
    .extend({
    id: areaCaptureIdSchema.optional(),
    audioNotes: z.array(audioNoteSchema).default([]),
    // Optional, no default — when omitted, the server preserves the
    // existing row's photoUrls instead of overwriting with []. The
    // captura page's audio-note save MUST omit this; the offline-queue
    // /upload-photo path is the source of truth for photoUrls and
    // would race-lose if the audio save passed through an empty list.
    // See ticket #0165.
    photoUrls: z.array(z.string().url()).optional(),
});
//# sourceMappingURL=area-capture.js.map